/**
 * Letter Scheduler Hook
 *
 * Manages the timing and sequencing of letter throws.
 * Handles accelerating rhythm, inter-word pauses, and skip functionality.
 *
 * Key behaviors:
 * - First letters of words are slower (dramatic effect)
 * - Subsequent letters accelerate
 * - Inter-word pauses create rhythm
 * - Max 2 concurrent flying letters (performance)
 * - Skip cancels all pending timeouts
 */

import { useRef, useCallback, useState, useEffect } from 'react';
import type { SpeedPreset } from '../types';
import { useSpeedPresets, getWordsForLine } from './useSpeedPresets';

interface ScheduledLetter {
  char: string;
  wordIndex: number;
  letterIndex: number;
  globalIndex: number;
  timeoutId: ReturnType<typeof setTimeout>;
}

interface UseLetterSchedulerOptions {
  text: string;
  speed: SpeedPreset;
  onLetterThrow: (char: string, wordIndex: number, letterIndex: number, globalIndex: number) => void;
  onWordComplete: (wordIndex: number) => void;
  onEmergenceComplete: () => void;
  autoStart?: boolean;
}

export function useLetterScheduler({
  text,
  speed,
  onLetterThrow,
  onWordComplete,
  onEmergenceComplete,
  autoStart = true,
}: UseLetterSchedulerOptions) {
  const { settings, getLetterDelay, getInterWordPause } = useSpeedPresets(speed);

  // State
  const [isRunning, setIsRunning] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [currentLetterIndex, setCurrentLetterIndex] = useState(0);
  const [flyingCount, setFlyingCount] = useState(0);

  // Refs for cleanup
  const scheduledTimeouts = useRef<ReturnType<typeof setTimeout>[]>([]);
  const wordsRef = useRef<string[]>([]);
  const emergenceWordsCount = useRef<number>(0);

  // Parse text into words
  useEffect(() => {
    wordsRef.current = text.split(/\s+/).filter(Boolean);
    emergenceWordsCount.current = getWordsForLine(speed);
  }, [text, speed]);

  /**
   * Clear all scheduled timeouts (for skip)
   */
  const clearAllTimeouts = useCallback(() => {
    scheduledTimeouts.current.forEach(clearTimeout);
    scheduledTimeouts.current = [];
  }, []);

  /**
   * Schedule a single letter throw
   */
  const scheduleLetter = useCallback(
    (
      char: string,
      wordIdx: number,
      letterIdx: number,
      globalIdx: number,
      delay: number
    ) => {
      const timeoutId = setTimeout(() => {
        onLetterThrow(char, wordIdx, letterIdx, globalIdx);
        setCurrentWordIndex(wordIdx);
        setCurrentLetterIndex(letterIdx);
        setFlyingCount((prev) => Math.min(prev + 1, 2)); // Max 2 concurrent
      }, delay);

      scheduledTimeouts.current.push(timeoutId);
    },
    [onLetterThrow]
  );

  /**
   * Schedule word completion callback
   */
  const scheduleWordComplete = useCallback(
    (wordIdx: number, delay: number) => {
      const timeoutId = setTimeout(() => {
        onWordComplete(wordIdx);

        // Check if emergence is complete
        if (wordIdx + 1 >= emergenceWordsCount.current) {
          setIsComplete(true);
          onEmergenceComplete();
        }
      }, delay);

      scheduledTimeouts.current.push(timeoutId);
    },
    [onWordComplete, onEmergenceComplete]
  );

  /**
   * Start the letter throwing sequence
   */
  const start = useCallback(() => {
    if (isRunning) return;

    const words = wordsRef.current;

    // P0 fix: Handle empty/whitespace-only text gracefully
    if (words.length === 0) {
      setIsComplete(true);
      onEmergenceComplete();
      return;
    }

    setIsRunning(true);
    setIsComplete(false);
    setCurrentWordIndex(0);
    setCurrentLetterIndex(0);

    const emergenceWords = words.slice(0, emergenceWordsCount.current);

    // P0 fix: Handle case where emergenceWords is empty
    if (emergenceWords.length === 0) {
      setIsComplete(true);
      onEmergenceComplete();
      return;
    }

    let cumulativeDelay = settings.multiplier >= 3.0 ? 200 : 500; // Initial delay
    let globalIndex = 0;

    emergenceWords.forEach((word, wordIdx) => {
      // P0 fix: Use Array.from for proper Unicode/emoji handling
      const letters = Array.from(word);

      letters.forEach((char, letterIdx) => {
        const letterDelay = getLetterDelay(letterIdx, wordIdx);
        cumulativeDelay += letterDelay;

        scheduleLetter(char, wordIdx, letterIdx, globalIndex, cumulativeDelay);
        globalIndex++;
      });

      // Schedule word completion after last letter lands
      const landingBuffer = 300; // Allow landing animation
      scheduleWordComplete(wordIdx, cumulativeDelay + landingBuffer);

      // Add inter-word pause
      cumulativeDelay += getInterWordPause();
    });
  }, [
    isRunning,
    settings,
    getLetterDelay,
    getInterWordPause,
    scheduleLetter,
    scheduleWordComplete,
    onEmergenceComplete,
  ]);

  /**
   * Skip to end - cancel all animations and jump to completion
   */
  const skip = useCallback(() => {
    clearAllTimeouts();
    setIsRunning(false);
    setIsComplete(true);
    setFlyingCount(0);
    onEmergenceComplete();
  }, [clearAllTimeouts, onEmergenceComplete]);

  /**
   * Reset scheduler state
   */
  const reset = useCallback(() => {
    clearAllTimeouts();
    setIsRunning(false);
    setIsComplete(false);
    setCurrentWordIndex(0);
    setCurrentLetterIndex(0);
    setFlyingCount(0);
  }, [clearAllTimeouts]);

  /**
   * Called when a letter finishes its landing animation
   */
  const onLetterLanded = useCallback(() => {
    setFlyingCount((prev) => Math.max(prev - 1, 0));
  }, []);

  // Auto-start if configured
  // P0 fix: Include all dependencies to prevent stale closure bugs
  useEffect(() => {
    if (autoStart && !isRunning && !isComplete) {
      start();
    }
    // Re-evaluate when autoStart, isRunning, or isComplete changes
  }, [autoStart, isRunning, isComplete, start]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearAllTimeouts();
    };
  }, [clearAllTimeouts]);

  return {
    // State
    isRunning,
    isComplete,
    currentWordIndex,
    currentLetterIndex,
    flyingCount,

    // Actions
    start,
    skip,
    reset,
    onLetterLanded,

    // Config
    emergenceWordsCount: emergenceWordsCount.current,
    totalWords: wordsRef.current.length,
  };
}

export type UseLetterSchedulerReturn = ReturnType<typeof useLetterScheduler>;
